import ChecklistIcon from "@mui/icons-material/Checklist";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import { CheckCircle, Schedule, Delete as DeleteIcon } from "@mui/icons-material";
import CustomButton from "components/shared/button/CustomButton";
import CustomContentDialog from "components/shared/dialog/CustomContentDialog";
import CustomTextField from "components/shared/textFields/CustomTextField";
import CheckListItem from "./ChecklistItem";
import { useProjectChecklist } from "./controller";
import { useTheme } from "@mui/material/styles";

import CustomDialog from "components/shared/dialog/CustomDialog";
import CustomAutoComplete from "components/shared/autoCompletes/CustomAutoComplete";

const ProjectChecklist = ({
  projectId,
  isDisabled,
  setDisabled,
  initializeLoadingToast,
  updateLoadingToast,
}) => {
  const theme = useTheme();
  const { checklistLoading, checklistData, ...controller } = useProjectChecklist({
    projectId,
    setDisabled,
    initializeLoadingToast,
    updateLoadingToast,
  });

  const items = checklistData?.checklistItems || [];
  const total = items.length;
  const completed = items.filter((i) => i.isChecked).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = percent === 100;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        bgcolor: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Dialogs */}
      <CustomContentDialog
        open={controller.createItemDialogOpen}
        handleClickClose={controller.handleCreateDialogItemClose}
        handleConfirmationClick={() => controller.handleCreateItem()}
        title="Adicionar Item ao Checklist"
        confirmationClickText="Adicionar"
        requestLoadingText="Adicionando..."
        requestLoading={controller.createLoading}
        confirmationDisabled={!controller.itemName.value.trim()}
        content={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Digite o conteúdo do novo item.
            </Typography>
            <form onSubmit={controller.handleFormSubmit} style={{ width: "100%" }}>
              <CustomTextField
                params={controller.itemName}
                label="Conteúdo do item"
                placeholder="Ex: Verificar documentação técnica..."
                maxLength={100}
                width="100%"
                required={true}
                error={controller.itemName.value !== "" && controller.itemName.value.trim() === ""}
                helperText={`${controller.itemName.value.length}/100`}
                sx={{ mt: 1 }}
                autoFocus
              />
              <button type="submit" style={{ display: 'none' }} />
            </form>
          </Box>
        }
      />
      <CustomDialog
        open={controller.createDialogOpen}
        handleClickClose={controller.handleCreateDialogClose}
        title="Criar novo checklist"
        contentText="Tem certeza que deseja criar um novo checklist para este projeto?"
        requestLoading={controller.createLoading}
        handleConfirmationClick={() => controller.handleCreateChecklist()}
        confirmationClickText="Criar"
        requestLoadingText="Criando..."
      />
      <CustomContentDialog
        open={controller.duplicateDialogOpen}
        handleClickClose={controller.handleDuplicateDialogClose}
        handleConfirmationClick={() => controller.handleDuplicateCheckList()}
        title="Duplicar checklist"
        confirmationClickText="Duplicar"
        requestLoadingText="Duplicando..."
        requestLoading={controller.createLoading}
        content={
          <CustomAutoComplete
            params={controller.template}
            options={controller.templatesData}
            label="Template"
            width="100%"
          />
        }
      />
      <CustomDialog
        open={controller.deleteDialogOpen}
        handleClickClose={controller.handleDeleteDialogClose}
        title="Excluir Checklist"
        contentText="As informações do checklist serão excluídas e não poderão ser recuperadas."
        requestLoading={controller.deleteLoading}
        handleConfirmationClick={() => controller.handleDeleteChecklist(checklistData.id)}
        confirmationClickText="Excluir"
        requestLoadingText="Excluindo..."
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Checklist
          </Typography>
          {checklistData && (
            <Chip
              label={`${completed}/${total}`}
              size="small"
              color={isComplete ? "success" : "default"}
              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
            />
          )}
        </Box>
        {checklistData && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Adicionar item">
              <IconButton
                size="small"
                onClick={controller.handleCreateItemDialogOpen}
                disabled={isDisabled || controller.createLoading}
                sx={{ color: theme.palette.primary.main }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir checklist">
              <IconButton
                size="small"
                onClick={controller.handleDeleteDialogOpen}
                disabled={isDisabled}
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Content */}
      {checklistLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !checklistData ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, px: 2, gap: 2 }}>
          <ChecklistIcon sx={{ fontSize: 40, color: "text.disabled" }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Nenhum checklist cadastrado
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => controller.handleCreateDialogOpen()}
              disabled={isDisabled}
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
            >
              Criar Novo
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => controller.handleDuplicateDialogOpen()}
              disabled={isDisabled}
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
            >
              Usar Template
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Progress bar compacta */}
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                    bgcolor: isComplete ? "success.main" : theme.palette.grey[500],
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem", minWidth: 28, textAlign: "right" }}
              >
                {percent}%
              </Typography>
            </Box>
          </Box>

          {/* Items list */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              maxHeight: "none",
              px: 0.5,
              pb: 0.5,
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "transparent",
                borderRadius: 3,
              },
              "&:hover::-webkit-scrollbar-thumb": {
                bgcolor: "action.disabled",
              },
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
              "&:hover": {
                scrollbarColor: `rgba(0,0,0,0.2) transparent`,
              },
            }}
          >
            {items.map((checklistItem, index) => (
              <CheckListItem
                key={checklistItem.id}
                id={checklistItem.id}
                content={checklistItem.content}
                isChecked={checklistItem.isChecked}
                index={index}
                projectId={projectId}
                handleDeleteItem={controller.handleDeleteItem}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectChecklist;
